import { assetAccumulatedTotal, assetGrossCost } from './fixedAssetFormRules';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper, Table, TableBody, TableCell,
  Checkbox, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { FixedAssetService } from '../../services/fixedAsset.service';
import {
  FixedAsset, FixedAssetStatus, FIXED_ASSET_STATUS_COLORS, assetCarrying,
} from '../../interfaces/fixedAsset/fixedAsset.interface';
import { humanizeEnum } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
// ---- F10 register filter ----
import SearchableSelect from '../common/SearchableSelect';
import CustodianPicker from './CustodianPicker';
import AssetLabelDialog from './AssetLabelDialog'; // Q1
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { CostCentreService } from '../../services/costCentre.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import { DepartmentOption } from '../../interfaces/costCentre.interface';
// ---- end F10 ----

const money = (n: number) =>
  (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FixedAssetList: React.FC = () => {
  const canManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE); // register / create = FIXED_ASSET_MANAGE server-side
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string[]>([]); // Q1 label selection
  const [labelOpen, setLabelOpen] = useState(false);
  const toast = useToast();
  const [rows, setRows] = useState<FixedAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<'ALL' | FixedAssetStatus>('ALL');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(10);
  // ---- F10: placement filter, applied on the server ----
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [departments, setDepartments] = useState<DepartmentOption[]>([]);
  const [locationId, setLocationId] = useState('');
  const [departmentId, setDepartmentId] = useState('');
  const [custodianUserId, setCustodianUserId] = useState('');
  const [custodianName, setCustodianName] = useState<string | null>(null);

  const load = async () => {
    const institutionId = AuthService.getSelectedInstitution();
    try {
      setLoading(true);
      const res = await FixedAssetService.listByInstitution(institutionId!, 0, 200, {
        locationId: locationId || undefined,
        departmentId: departmentId || undefined,
        custodianUserId: custodianUserId || undefined,
      });
      setRows(res.content);
    } catch (e) {
      toast.showApiError(e, 'Failed to load fixed assets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [locationId, departmentId, custodianUserId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    FixedAssetTransferService.locations().then(r => setLocations(Array.isArray(r) ? r : []))
      .catch(e => { setLocations([]); toast.showApiError(e, 'Failed to load asset locations'); });
    CostCentreService.getDepartments().then(r => setDepartments(Array.isArray(r) ? r : []))
      .catch(e => { setDepartments([]); toast.showApiError(e, 'Failed to load departments'); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const cat = category.trim().toLowerCase();
    return rows.filter(r =>
      (statusFilter === 'ALL' || r.status === statusFilter) &&
      (cat === '' || (r.category ?? '').toLowerCase().includes(cat)));
  }, [rows, statusFilter, category]);

  const paged = filtered.slice(page * size, page * size + size);

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.list.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Fixed Assets</Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button data-help="fixedAssets.list.printLabels" variant="outlined" disabled={selected.length === 0}
            onClick={() => setLabelOpen(true)}>
            Print labels{selected.length > 0 ? ` (${selected.length})` : ''}
          </Button>
          <Button data-help="fixedAssets.list.counts" variant="outlined" onClick={() => navigate('/fixed-assets/counts')}>
            Asset counts
          </Button>
        </Box>
        {canManage && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button data-help="fixedAssets.list.register" variant="outlined" onClick={() => navigate('/fixed-assets/register')}>
              Register Existing
            </Button>
            <Button data-help="fixedAssets.list.new" variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/fixed-assets/new')}>
              New Asset (Purchase)
            </Button>
          </Box>
        )}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="fixedAssets.lst.filter">
        <TextField
          data-help="fixedAssets.lst.filter.status" select label="Status" size="small" value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as 'ALL' | FixedAssetStatus); setPage(0); }}
          sx={{ flex: '1 1 180px', maxWidth: 240 }}
        >
          <MenuItem value="ALL">All</MenuItem>
          <MenuItem value="ACTIVE">Active</MenuItem>
          <MenuItem value="FULLY_DEPRECIATED">Fully depreciated</MenuItem>
          <MenuItem value="DISPOSED">Disposed</MenuItem>
        </TextField>
        <TextField
          data-help="fixedAssets.lst.filter.category" label="Category" size="small" value={category}
          onChange={(e) => { setCategory(e.target.value); setPage(0); }}
          sx={{ flex: '1 1 180px', maxWidth: 260 }}
        />
        {/* ---- F10 placement filter ---- */}
        <SearchableSelect<AssetLocation>
          data-help="fixedAssets.lst.filter.location" label="Location" options={locations} value={locationId}
          onChange={id => { setLocationId(id); setPage(0); }}
          getOptionLabel={l => `${l.code} - ${l.name}`} getOptionId={l => l.id}
          sx={{ flex: '1 1 220px', maxWidth: 300 }} data-testid="filter-location" />
        <SearchableSelect<DepartmentOption>
          data-help="fixedAssets.lst.filter.department" label="Department" options={departments} value={departmentId}
          onChange={id => { setDepartmentId(id); setPage(0); }}
          getOptionLabel={d => d.name} getOptionId={d => d.id}
          sx={{ flex: '1 1 220px', maxWidth: 300 }} />
        <CustodianPicker data-help="fixedAssets.lst.filter.custodian" value={custodianUserId} valueLabel={custodianName}
          onChange={(id, o) => { setCustodianUserId(id); setCustodianName(o?.name ?? null); setPage(0); }}
          sx={{ flex: '1 1 220px', maxWidth: 300 }} />
        {/* ---- end F10 ---- */}
      </Box>

      {loading ? <CircularProgress /> : (
        <TableContainer component={Paper}>
          <Table data-help-section="fixedAssets.lst.table">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" data-help="fixedAssets.lst.col.select">
                  <Checkbox data-help="fixedAssets.lst.selectAll" size="small" inputProps={{ 'aria-label': 'Select all on this page' }}
                    checked={paged.length > 0 && paged.every(r => selected.includes(r.id))}
                    indeterminate={paged.some(r => selected.includes(r.id)) && !paged.every(r => selected.includes(r.id))}
                    onChange={(e) => setSelected(prev => e.target.checked
                      ? Array.from(new Set([...prev, ...paged.map(r => r.id)]))
                      : prev.filter(x => !paged.some(r => r.id === x)))} />
                </TableCell>
                <TableCell data-help="fixedAssets.lst.col.code">Code</TableCell>
                <TableCell data-help="fixedAssets.lst.col.name">Name</TableCell>
                <TableCell data-help="fixedAssets.lst.col.category">Category</TableCell>
                <TableCell data-help="fixedAssets.lst.col.location">Location</TableCell>
                <TableCell data-help="fixedAssets.lst.col.custodian">Custodian</TableCell>
                <TableCell data-help="fixedAssets.lst.col.acquired">Acquired</TableCell>
                <TableCell data-help="fixedAssets.lst.col.cost" align="right">Cost</TableCell>
                <TableCell data-help="fixedAssets.lst.col.accum" align="right">Accumulated (incl. impairment)</TableCell>
                <TableCell data-help="fixedAssets.lst.col.nbv" align="right">NBV</TableCell>
                <TableCell data-help="fixedAssets.lst.col.status">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.map(r => (
                <TableRow key={r.id} hover sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/fixed-assets/${r.id}`)}>
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox data-help="fixedAssets.lst.action.select" size="small" checked={selected.includes(r.id)}
                      inputProps={{ 'aria-label': `Select ${r.assetCode}` }}
                      onChange={(e) => setSelected(prev => e.target.checked ? [...prev, r.id] : prev.filter(x => x !== r.id))} />
                  </TableCell>
                  <TableCell>{r.assetCode}</TableCell>
                  <TableCell>{r.name}</TableCell>
                  <TableCell>{r.category ?? '—'}</TableCell>
                  <TableCell>{r.locationName ?? '—'}</TableCell>
                  <TableCell>{r.custodianName ?? '—'}</TableCell>
                  <TableCell>{formatDate(r.acquisitionDate)}</TableCell>
                  <TableCell align="right">{money(assetGrossCost(r))}</TableCell>
                  <TableCell align="right">{money(assetAccumulatedTotal(r))}</TableCell>
                  <TableCell align="right">{money(assetCarrying(r))}</TableCell>
                  <TableCell>
                    <Chip size="small" label={humanizeEnum(r.status)} color={FIXED_ASSET_STATUS_COLORS[r.status]} />
                  </TableCell>
                </TableRow>
              ))}
              {paged.length === 0 && (
                <TableRow><TableCell colSpan={11} align="center">No assets found.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={size}
            onRowsPerPageChange={(e) => { setSize(Number(e.target.value)); setPage(0); }}
          />
        </TableContainer>
      )}
      {labelOpen && <AssetLabelDialog open assetIds={selected} onClose={() => setLabelOpen(false)} />}
    </Box>
  );
};

export default FixedAssetList;
